/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react'

import React from 'react'
import { View, Icon, Pressable } from '@components/atoms'
import Popover from '@mui/material/Popover'

interface PropsType {
	className?: string
	picture: string
	name: string
	color: string
	icon?: JSX.Element
	extpandingDisable?: boolean
	/** 지름(px). 원·이니셜·아이콘 크기가 전부 이 값에서 나온다. 기본 40 */
	size?: number
	/** 이니셜·아이콘 색. 안 주면 배경 명도를 보고 흰색/잉크 중에 고른다 */
	textColor?: string
}

interface FacePropsType {
	picture: string
	name: string
	backgroundColor: string
	textColor: string
	icon?: JSX.Element
	size: number
}

const FALLBACK_COLOR = '#bdbdbd'

/**
 * 배경 위에서 읽히는 글자색을 고른다.
 *
 * 고객 색은 통제된 팔레트(전부 흰 글자 대비 6:1 이상)라 늘 흰색이 나오지만,
 * 백오피스 USERS 쪽에는 아직 `randomColor()` 로 들어간 값이 남아 있다 —
 * `#ede212` 같은 형광색 위에서는 흰 글자가 보이지 않는다.
 * 그래서 색을 넘겨받는 쪽이 아니라 **원을 그리는 쪽**에서 판단한다.
 *
 * 기준은 WCAG 상대 휘도. 0.5 는 흰색/검정 대비가 갈리는 지점 부근이다.
 */
const readableTextColor = (backgroundColor: string): string => {
	const hex: string = backgroundColor.replace('#', '')
	const full: string =
		hex.length === 3
			? hex
					.split('')
					.map(aChar => aChar + aChar)
					.join('')
			: hex

	if (full.length !== 6 || /[^0-9a-fA-F]/.test(full)) {
		return '#FFFFFF'
	}

	const channels: number[] = [0, 2, 4].map(i => {
		const value: number = parseInt(full.slice(i, i + 2), 16) / 255
		return value <= 0.03928 ? value / 12.92 : Math.pow((value + 0.055) / 1.055, 2.4)
	})
	const luminance: number =
		0.2126 * channels[0] + 0.7152 * channels[1] + 0.0722 * channels[2]

	return luminance > 0.45 ? '#16181B' : '#FFFFFF'
}

/**
 * 아바타의 원 그 자체. 사진 → 이니셜 → 넘겨받은 아이콘 → 기본 아이콘 순으로 떨어진다.
 *
 * Avatar 바깥에 둔다 — 안에 두면 렌더마다 새 컴포넌트 타입이 되어 DOM 이 통째로 다시
 * 붙고, 사진이 매번 다시 로드되면서 깜빡인다.
 */
const AvatarFace = (props: FacePropsType): JSX.Element => {
	const { picture, name, backgroundColor, textColor, size } = props

	// 깨진 URL 이면 이니셜로 내려간다. 사진이 바뀌면 다시 시도한다.
	const [isBroken, setIsBroken] = React.useState<boolean>(false)

	React.useEffect(() => {
		setIsBroken(false)
	}, [picture])

	const trimmedName: string = name.trim()
	const showPicture: boolean = picture !== '' && !isBroken

	// 이니셜은 name 의 첫 글자를 **저장된 그대로** 쓴다 — 대문자로 바꾸지 않는다.
	// (고객 60명 중 53명의 이름이 소문자로 시작한다: 'daniel' 은 'd' 로 보여야 한다)
	// charAt(0) 이 아니라 Array.from 인 이유: charAt 은 UTF-16 코드 유닛 하나를 떼어 내서
	// 이모지처럼 서로게이트 쌍인 글자를 반쪽으로 쪼갠다(깨진 사각형이 뜬다).
	const initial: string = trimmedName === '' ? '' : Array.from(trimmedName)[0]

	return (
		<View
			className="avatarFace"
			css={{
				position: 'relative',
				display: 'flex',
				alignItems: 'center',
				justifyContent: 'center',
				flexShrink: 0,
				width: size,
				height: size,
				borderRadius: '50%',
				overflow: 'hidden',
				backgroundColor: backgroundColor,
				color: textColor,
				fontFamily: '"Roboto","Helvetica","Arial",sans-serif',
				fontSize: Math.max(11, Math.round(size * 0.4)),
				fontWeight: 500,
				lineHeight: 1,
				userSelect: 'none',
			}}
		>
			{showPicture ? (
				<img
					src={picture}
					alt={trimmedName === '' ? '' : `${trimmedName} avatar`}
					onError={() => setIsBroken(true)}
					css={{
						display: 'block',
						width: '100%',
						height: '100%',
						// 원본 비율과 무관하게 짧은 변에 맞춰 가운데를 채운다 —
						// 여백이 생겨 배경색이 비치는 일이 없다(서버도 같은 규칙으로 자른다).
						objectFit: 'cover',
						objectPosition: 'center',
						// 부모의 overflow:hidden 만으로도 잘리지만, 브라우저에 따라
						// 둥근 모서리에서 1px 실선이 남는다. 이미지 자체도 둥글게 잘라 둔다.
						borderRadius: '50%',
					}}
				/>
			) : initial !== '' ? (
				<span aria-hidden="true">{initial}</span>
			) : props.icon !== undefined ? (
				props.icon
			) : (
				<Icon
					name="account"
					css={{ color: 'inherit', fontSize: Math.round(size * 0.58) }}
				/>
			)}
		</View>
	)
}

/**
 * 원형 아바타.
 *
 * 크기는 `size` 하나가 정한다. 예전에는 바깥 버튼이 40, 안쪽 원이 41 로 어긋나 있어서
 * className 으로 지름을 줄이면 안쪽 원이 버튼 밖으로 삐져나왔고, 이니셜은 지름과 무관하게
 * 1.25rem 으로 고정이라 작은 아바타에서 글자가 원을 넘쳤다.
 *
 * 확대 팝오버는 사진이 있을 때만 연다 — 이니셜 한 글자를 크게 띄우는 건 볼 게 없다.
 */
export const Avatar = (props: PropsType) => {
	const size: number = props.size ?? 40
	const name: string = props.name ?? ''
	const picture: string = props.picture ?? ''
	const backgroundColor: string = props.color === '' ? FALLBACK_COLOR : props.color
	const textColor: string = props.textColor ?? readableTextColor(backgroundColor)
	const isExpandable: boolean = !(props.extpandingDisable ?? false) && picture !== ''

	const [anchorEl, setAnchorEl] = React.useState<HTMLButtonElement | null>(null)

	const open = Boolean(anchorEl)
	const id = open ? 'avatar-popover' : undefined

	const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
		setAnchorEl(event.currentTarget)
	}

	const handleClose = () => {
		setAnchorEl(null)
	}

	const face = (
		<AvatarFace
			picture={picture}
			name={name}
			backgroundColor={backgroundColor}
			textColor={textColor}
			icon={props.icon}
			size={size}
		/>
	)

	if (!isExpandable) {
		return (
			<View css={style} className={`avatar ${props.className ?? ''}`}>
				{face}
			</View>
		)
	}

	return (
		<View css={style} className={`avatar ${props.className ?? ''}`}>
			<Pressable
				className="avatarButton"
				css={{ width: size, height: size }}
				aria-describedby={id}
				aria-label={name === '' ? 'avatar' : `${name} avatar`}
				onClick={handleClick}
			>
				{face}
			</Pressable>

			<Popover
				id={id}
				open={open}
				anchorEl={anchorEl}
				onClose={handleClose}
				anchorOrigin={{
					vertical: 'bottom',
					horizontal: 'left',
				}}
				PaperProps={{ sx: { borderRadius: '50%', overflow: 'hidden' } }}
			>
				<AvatarFace
					picture={picture}
					name={name}
					backgroundColor={backgroundColor}
					textColor={textColor}
					icon={props.icon}
					size={200}
				/>
			</Popover>
		</View>
	)
}

const style = css({
	position: 'relative',
	width: 'fit-content',

	'.avatarButton': {
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
		padding: 0,
		borderRadius: '50%',
		overflow: 'hidden',
	},
})
